import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-585bbadc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "scroll-container" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fa-regular fa-angle-up" }, null, -1))
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fa-regular fa-angle-down" }, null, -1))
const _hoisted_5 = [
  _hoisted_4
]

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { ScrollbarPosition } from "@/enums/system/ScrollbarPosition";
import { DxScrollView } from "devextreme-vue/scroll-view";


export default /*#__PURE__*/_defineComponent({
  __name: 'scroll-view',
  props: {
  /**
   * The boolean value indicates whether the component should reset the scroll position.
   * @type {boolean}
   * @default false
   */
  resetScroll: {
    type: Boolean,
    default: false,
  },

  /**
   * The boolean value indicates whether the component should automatically scroll to the bottom.
   * @type {boolean}
   * @default false
   */
  scrollToBottom: {
    type: Boolean,
    default: false,
  },

  /**
   * The CSS selector string for the scrollable element inside the component.
   * @type {string}
   * @default ''
   */
  scrollableElementSelector: {
    type: String,
    default: "",
  },

  /**
   * The direction of scrolling, either 'vertical' or 'horizontal'.
   * @type {string}
   * @default 'vertical'
   */
  scrollDirection: {
    type: String,
    default: "vertical",
  },

  /**
   * If the scrollbar should be displayed; 'onScroll', 'onHover', 'always', or 'never'.
   * @type {String}
   * @default "onScroll"
   */
  showScrollbar: {
    type: String,
    default: "onScroll",
  },

  /**
   * The boolean value indicates whether the component should hide the blur effect on the scroll content.
   * @type {boolean}
   * @default false
   */
  hideBlur: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:resetScroll"],
  setup(__props, { emit }) {

const props = __props;





const reset = computed({
  get: () => props.resetScroll,
  set: (value: Boolean) => emit("update:resetScroll", value),
});

const contentContainer = ref<HTMLElement | null>(null);
const observer = ref<MutationObserver | null>(null);

onMounted(() => {
  setTimeout(() => {
    onScroll();
  }, 750);
});

onMounted(() => {
  nextTick(() => onScroll());

  if (contentContainer.value) {
    observer.value = new MutationObserver(() => {
      nextTick(() => onScroll());
    });
    observer.value.observe(contentContainer.value, {
      attributes: true,
      subtree: true,
    });
  }
});

onBeforeUnmount(() => {
  if (observer.value) observer.value?.disconnect();
});

const scrollPosition = ref<ScrollbarPosition>(ScrollbarPosition.None);
const dxScrollView = ref<DxScrollView>();
const scrollInterval = ref(0);

/** Gets the class for fading content based on current scrollbar position */
const modalFadeClasses = computed(() => {
  if (props.hideBlur) {
    return "";
  }

  switch (scrollPosition.value) {
    case ScrollbarPosition.Top:
      return "atTop";
    case ScrollbarPosition.Bottom:
      return "atBottom";
    case ScrollbarPosition.Mid:
      return "midScroll";
    default:
      return "";
  }
});

// Watch for when parent wants to scroll back to the top of the view
watch(
  () => props.resetScroll,
  () => {
    if (!props.resetScroll || !dxScrollView.value?.instance) {
      return;
    }
    dxScrollView.value.instance.scrollTo(0);

    // Must always reset the v-model to false, otherwise subsequent resets won't fire
    reset.value = false;
  },
);

watch(
  () => props.scrollToBottom,
  (newVal: boolean, oldVal: boolean) => {
    // As content may load asynchronously, we need to continue scrolling until told to stop
    if (newVal) {
      scrollInterval.value = startAutoScroll();
      return;
    }

    /* When auto scroll is disabled, stop the interval running to avoid frustrating users
     * by resetting scroll position after manually scrolling */
    if (!newVal && oldVal && scrollInterval.value) {
      clearInterval(scrollInterval.value);
      return;
    }
  },
);

const startAutoScroll = () =>
  setInterval(() => {
    if (!props.scrollableElementSelector || !dxScrollView.value?.instance) {
      return;
    }

    const elem: HTMLDivElement | null = document.querySelector<HTMLDivElement>(
      props.scrollableElementSelector,
    );

    if (!elem) {
      return;
    }

    dxScrollView.value.instance.scrollTo(elem.scrollHeight);
  }, 250);

// Whenever the user scrolls, we calculate if they are at the top or bottom of the content, and can display the blur/arrows accordingly
const onScroll = () => {
  const inst = dxScrollView.value?.instance;
  if (!inst) return;

  const top = inst.scrollTop();
  const max = Math.floor(inst.scrollHeight() - inst.clientHeight());

  if (max <= 0) {
    scrollPosition.value = ScrollbarPosition.None;
  } else if (top <= 0) {
    scrollPosition.value = ScrollbarPosition.Top;
  } else if (top >= max) {
    scrollPosition.value = ScrollbarPosition.Bottom;
  } else if (top <= max && top > 0) {
    scrollPosition.value = ScrollbarPosition.Mid;
  } else {
    scrollPosition.value = ScrollbarPosition.None;
  }
};

const scrollToTopClick = () => {
  dxScrollView.value?.instance?.scrollTo(0);
};

const scrollToBottomClick = () => {
  const inst = dxScrollView.value?.instance;
  if (!inst) return;
  // The latter part of this calculates the position of the bottom of the content
  dxScrollView.value?.instance?.scrollTo(
    Math.ceil(inst.scrollHeight() - inst.clientHeight()),
  );
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (
        scrollPosition.value !== _unref(ScrollbarPosition).Top &&
        scrollPosition.value !== _unref(ScrollbarPosition).None
      )
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "scroll-icon top-icon",
          onClick: scrollToTopClick
        }, _hoisted_3))
      : _createCommentVNode("", true),
    (
        scrollPosition.value !== _unref(ScrollbarPosition).Bottom &&
        scrollPosition.value !== _unref(ScrollbarPosition).None
      )
      ? (_openBlock(), _createElementBlock("button", {
          key: 1,
          class: "scroll-icon bottom-icon",
          onClick: scrollToBottomClick
        }, _hoisted_5))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(['scroll-container', modalFadeClasses.value])
    }, [
      _createVNode(_unref(DxScrollView), {
        scrollByThumb: true,
        scrollByContent: false,
        ref_key: "dxScrollView",
        ref: dxScrollView,
        height: "100%",
        width: "100%",
        onScroll: onScroll,
        direction: props.scrollDirection,
        showScrollbar: props.showScrollbar
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: "content-container",
            ref_key: "contentContainer",
            ref: contentContainer
          }, [
            _renderSlot(_ctx.$slots, "default")
          ], 512)
        ]),
        _: 3
      }, 8, ["direction", "showScrollbar"])
    ], 2)
  ]))
}
}

})